import classNames from 'classnames';
import { routerActions } from 'connected-react-router';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import * as api from '../../api';
import { getPresence, MinDate, PresenceDialog } from '../../api';
import { usePresence } from '../../app-hooks';
import { useSelector } from '../../hook-utils';
import { Presence, Region } from '../../model';
import { selectDateFormating, selectPresenceMap, selectRegions } from '../../store/reducers/config';
import Button from '../Button';
import Calendar from '../Calendar';
import Checkbox from '../Checkbox';
import { ReactComponent as CheckIcon } from '../icons/menu-check.svg';
import { Menu, MenuItem, MenuTarget, targetFromEvent } from '../Menu';
import PresenceIcon from '../PresenceIcon';
import './PresencePage.scss';

type MenuState = { target: MenuTarget, type: Presence }

interface PresencePageParams {
  id: string
  server: string
}

const Minutes = Array.from(new Array(96)).map((_, i) => i * 15);

export default function PresencePage() {
  const dispatch = useDispatch()
  const { id, server } = useParams<PresencePageParams>() as PresencePageParams
  const { presenceValues, breakCodes, extendedAvailable } = usePresence({ id, server })
  const regions = useSelector(selectRegions)
  const [menu, setMenu] = useState<MenuState>()
  const avalibleRef = useRef<HTMLDivElement>(null)
  const breakCodeRef = useRef<HTMLDivElement>(null)
  const format = useSelector(selectDateFormating)
  const presenceMap = useSelector(selectPresenceMap)

  const [presence, setPresence] = useState<PresenceDialog | undefined>()

  useEffect(() => {
    let mounted = true

    load()
    return () => { mounted = false }

    async function load() {
      const data = await getPresence({ id, server })
      if (!mounted) { return }
      setPresence(data)
    }

  }, [id, server])

  if (!presence || !regions) return null

  const extendedMenu = extendedAvailable && extendedAvailable.map(p => <MenuItem key={p.type} onClick={() => {
    setPresence({
      ...presence, location: Presence.Available, currentDeviceGroupType: p.type, isIndefinite: true,
      returnTimeDate: dayjs(defaultReturnTime(0, presence.regionId, regions, presence.businessExcludeDays, presence.bstHours, presence.bstMinutes)).valueOf(),
    })
  }}>
    <PresenceIcon presence={Presence.Available} deviceGroupType={p.type} />
    {p.name}
  </MenuItem>)

  const breakMenu = breakCodes && breakCodes.map(b => <MenuItem key={b.id}
    onClick={() => {
      if (!regions || !presence.regionId) return

      setPresence({
        ...presence,
        location: Presence.Break,
        breakcode: b.id,
        isIndefinite: false,
        returnTimeDate: dayjs(defaultReturnTime(presenceMap.presence[Presence.Break].defaultReturnTimeId, presence.regionId, regions, presence.businessExcludeDays, presence.bstHours, presence.bstMinutes)).valueOf(),
      })

    }}
    noIcon={!isBreak(b.id)}>
    {isBreak(b.id) && <CheckIcon />}
    {b.name}
  </MenuItem>)

  const buttons = presenceValues.map(p => {
    return <div ref={p.presence === Presence.Available ? avalibleRef : p.presence === Presence.Break ? breakCodeRef : undefined} className={classNames(presence.location === p.presence && 'selected')} key={p.presence} onClick={(e) => {
      if ((p.presence === Presence.Available && extendedMenu) || (p.presence === Presence.Break && breakMenu)) {
        setMenu({ target: targetFromEvent(e), type: p.presence })
      } else {
        if (!regions || !presence.regionId) return

        if (p.presence === Presence.Queue || p.presence === Presence.Available) {
          setPresence({
            ...presence, location: p.presence, isIndefinite: true,
            returnTimeDate: dayjs(defaultReturnTime(0, presence.regionId, regions, presence.businessExcludeDays, presence.bstHours, presence.bstMinutes)).valueOf(),
          })
        } else {
          setPresence({
            ...presence, location: p.presence, isIndefinite: false,
            returnTimeDate: dayjs(defaultReturnTime(presenceMap.presence[p.presence].defaultReturnTimeId, presence.regionId, regions, presence.businessExcludeDays, presence.bstHours, presence.bstMinutes)).valueOf(),
          })
        }
      }
    }}>
      <PresenceIcon presence={p.presence} />
    </div>
  })

  if (format === null) return null
  const now = new Date()
  console.info('ReturnTimeDate ' + presence.returnTimeDate)
  const d = MinDate !== presence.returnTimeDate ? dayjs(presence.returnTimeDate) : dayjs(now)
  const defaultOptionValue = d.diff(dayjs(presence.returnTimeDate).set('hour', 0).set('minute', 0).set('millisecond', 0), 'minute')
  console.info('DefaultOptionValue ' + defaultOptionValue)

  return <div className='PresencePage'>
    <div className='presence'>{buttons}</div>
    <Calendar disabled={presence.location === Presence.Queue || presence.location === Presence.Available || presence.isIndefinite} value={presence.returnTimeDate === undefined || presence.returnTimeDate === MinDate ? Date.now() : presence.returnTimeDate}
      onChange={(value) => {
        setPresence({
          ...presence,
          returnTimeDate: value,
        })
      }} />
    <div className='time'>
      <Checkbox title='Indefinite' label='Indefinite' disabled={presence.location === Presence.Queue || presence.location === Presence.Available} checked={presence.isIndefinite === true} onChange={(e) => {
        setPresence({ ...presence, isIndefinite: e.currentTarget.checked })
      }
      } />
      <select disabled={presence.location === Presence.Queue || presence.location === Presence.Available || presence.isIndefinite}
        onChange={e => {
          const date = d.hour(0).millisecond(0).minute(parseInt(e.currentTarget.value));
          setPresence({
            ...presence,
            returnTimeDate: date.valueOf(),
          })
        }}
      >
        {Minutes.map(min => {
          const time = formatter(min)
          return <option key={min} value={min} selected={min === defaultOptionValue} >{time}</option>
        })}
      </select>
    </div>
    <div>
      <textarea onChange={e => {
        setPresence({
          ...presence,
          operatorNotes: e.currentTarget.value,
        })
      }} value={presence.operatorNotes} placeholder='Operator Notes' />
    </div>
    <div>
      <Button title='Set presence' primary={true} onClick={(e) => {
        api.setPresence({ id, server }, presence)
        //this needs to change when we have more then the directory sections
        dispatch(routerActions.replace('/directory'))
      }
      }>Ok</Button>
      <Button title='Cancel' onClick={() => {
        //this needs to change when we have more then the directory sections
        dispatch(routerActions.replace('/directory'))
      }}>Cancel</Button>
    </div>

    {menu && <Menu open={true} target={menu.type === Presence.Available ? avalibleRef.current : menu.type === Presence.Break ? breakCodeRef.current : null} placement='bottom-start' onClose={() => setMenu(undefined)}>
      {menu.type === Presence.Available ? extendedMenu : menu.type === Presence.Break ? breakMenu : undefined}
    </Menu>}
  </div>

  function isBreak(bc: number) {
    return presence && presence.location === Presence.Break && presence.breakcode === bc
  }
}

function formatter(min: number) {
  return dayjs.utc(0).add(min, 'm').format('LT')
}

function defaultReturnTime(defaultReturnTimeId: number, regionId: string, regions: Region[], businessExcludeDays: string, startingDayHours: number, startingDayMinutes: number) {
  const adjustedLocalTime = adjustTimezoneOffset(new Date(), regionId, regions);

  switch (defaultReturnTimeId) {
    case 0:
      return dayjs(roundNearest(adjustedLocalTime, 15)).valueOf()
    case 1:
      return dayjs(roundNearest(adjustedLocalTime, 15)).add(15, 'minute').valueOf()
    case 2:
      return dayjs(roundNearest(adjustedLocalTime, 15)).add(30, 'minute').valueOf()
    case 3:
      return dayjs(roundNearest(adjustedLocalTime, 30)).add(60, 'minute').valueOf()
    case 4:
      const dt = getNextWorkingDay(adjustedLocalTime, businessExcludeDays);
      return dt.setHours(startingDayHours, startingDayMinutes, 0, 0);
  }
}

function getNextWorkingDay(dt: Date, businessExcludeDays: string) {

  // businessExcludeDays_ 1-7
  // Date().getDay() 0-6
  const now = dt;
  let next = (now.getDay() === 6) ? 0 : now.getDay() + 1;
  let i = 1;
  for (i; i < 8; i++) {
    if (businessExcludeDays.indexOf((next + 1).toString()) !== -1) {
      next = (next === 6) ? 0 : next + 1;
    } else {
      break;
    }
  }
  now.setDate(now.getDate() + i);
  return now;
}

function roundNearest(dt: Date, intervalMin: number) {
  const coeff = 1000 * 60 * intervalMin;
  return new Date(Math.round(dt.getTime() / coeff) * coeff);;
}

function adjustTimezoneOffset(tm: Date, regionId: string, regions: Region[]) {
  const d = new Date();
  const browserOffset = d.getTimezoneOffset(); //should be always negative
  let regionOffset = browserOffset;
  for (let index = 0; index < regions.length; index++) {
    const region = regions[index];
    if (region.id === regionId) {
      regionOffset = region.timeBias;
      index = regions.length;
    }
  }

  const offset = regionOffset + browserOffset;
  tm.setMinutes(tm.getMinutes() + offset);
  return tm;
}
